import type { Message } from "~/components/assistant/types";

export interface ProcessingOptions {
  nextMessageId: () => string;
  traceId?: string;
  debugMode?: boolean;
}

export interface ProcessingResult {
  messages: Message[];
  // Optional: auto-generated conversation name from the backend
  conversationName?: {
    conversationId: string;
    displayName: string;
  };
}

export interface MessageTypeRenderer {
  type: string;
  component: React.ComponentType<any>;

  // Optional: process stream events for this type
  processEvent?: (
    messages: Message[],
    event: any,
    options: ProcessingOptions
  ) => ProcessingResult | null;
}

const registry = new Map<string, MessageTypeRenderer>();

/**
 * Register a custom message type renderer.
 * Call during app initialization.
 */
export function registerMessageType(renderer: MessageTypeRenderer): void {
  if (registry.has(renderer.type)) {
    console.warn(`Message type "${renderer.type}" already registered`);
    return;
  }
  registry.set(renderer.type, renderer);
}

/**
 * Get renderer for a message type.
 */
export function getMessageTypeRenderer(type: string): MessageTypeRenderer | undefined {
  return registry.get(type);
}

/**
 * Get all registered message type renderers.
 */
export function getAllMessageTypeRenderers(): MessageTypeRenderer[] {
  return Array.from(registry.values());
}

/**
 * Process stream event using registered processors.
 * Returns null if no processor handles the event.
 */
export function processStreamEventWithRegistry(
  messages: Message[],
  event: any,
  options: ProcessingOptions
): ProcessingResult | null {
  for (const renderer of registry.values()) {
    if (renderer.processEvent) {
      const result = renderer.processEvent(messages, event, options);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

/**
 * Clear all registered message types.
 * Useful for testing - allows resetting registry state between tests.
 */
export function clearRegistry(): void {
  registry.clear();
}
